// What this customer has that belongs to no line.
//
// WHY THIS EXISTS AT ALL. The customer page used to carry a link reading "Show account-wide
// numbers, routing, extensions and messaging", and behind it five sections listing every
// number and every extension the customer owned. Kevin, 2026-08-13: get rid of it and fold it
// into the normal workflow of adding and removing a line.
//
// Almost all of it folded cleanly, because a line IS a number plus a package: the Lines table
// above is the list of numbers, and each line's own page carries its routing, extensions,
// voicemail and messaging. But two things have no line to fold into — a number assigned to
// this customer that no line claims, and an extension that rings for nothing — and the
// drawer was the ONLY place either showed.
//
// Deleting it would have hidden them rather than moved them, which is how a number gets paid
// for and never used, and how an extension outlives the line that explains it. So they
// surface here as WORK TO DO rather than as a table to browse: nothing renders at all when
// there is nothing wrong, and each row says what the operator should do about it.

import { Link } from 'react-router-dom';

const digits = (v) => String(v || '').replace(/\D/g, '');
const fmt = (e164) => {
  const d = digits(e164);
  return d.length === 11 && d.startsWith('1')
    ? `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`
    : e164;
};

export default function UnattachedThings({
  dids = [], lines = [], sipAccounts = [], subscriberId, backHere, canAdd, onAddLine,
}) {
  const claimed = new Set(lines.map((l) => digits(l.number)).filter(Boolean));
  const spareNumbers = dids.filter((d) => !claimed.has(digits(d.e164)));

  // `ringsFor` is the server's answer to "which line does this extension ring for", covering
  // both the explicit list and the fallback. Deriving it here from `line_extensions` alone is
  // what made the picker offer two live extensions as spare.
  const spareExtensions = sipAccounts.filter((a) => !a.ringsFor && a.status !== 'deleted');

  // IS IT A SPARE, OR A DUPLICATE OF ONE THAT ALREADY RINGS?
  //
  // Lyons Fire, 2026-08-14: this listed `13038236611` as spare while an extension of the
  // SAME NAME was ringing on line 137 — two rows for one name, and the advice below said
  // "attach it on a line's Routing tab", which would have left the line ringing both. The
  // create path no longer produces these (it adopts instead), but the ones already out there
  // still have to be described correctly, and "remove it" is the only right answer for a
  // duplicate.
  const ringing = new Set(
    sipAccounts.filter((a) => a.ringsFor).map((a) => String(a.username)),
  );
  const isDuplicate = (a) => ringing.has(String(a.username));

  if (!spareNumbers.length && !spareExtensions.length) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-5">
      <h2 className="text-sm font-semibold text-amber-900">Not on any line</h2>
      <p className="mt-1 text-xs text-amber-900">
        This customer owns these, and nothing is using them. Each one is either work not
        finished or something to give back.
      </p>

      {spareNumbers.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-medium uppercase tracking-wide text-amber-900">
            {spareNumbers.length === 1 ? 'A number with no line' : `${spareNumbers.length} numbers with no line`}
          </h3>
          <ul className="mt-2 space-y-1.5">
            {spareNumbers.map((d) => (
              <li key={d.e164} className="flex flex-wrap items-baseline gap-x-3 text-sm">
                <Link
                  to={`/dids/${encodeURIComponent(digits(d.e164))}`}
                  state={backHere}
                  className="font-mono text-blue-700 hover:underline"
                >
                  {fmt(d.e164)}
                </Link>
                <span className="text-xs text-amber-900">
                  assigned here, but no line claims it — nothing answers a call to it.
                </span>
              </li>
            ))}
          </ul>
          {canAdd && (
            <button
              type="button"
              onClick={onAddLine}
              className="mt-2 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Add a line for one of these
            </button>
          )}
        </div>
      )}

      {spareExtensions.length > 0 && (
        <div className="mt-5">
          <h3 className="text-xs font-medium uppercase tracking-wide text-amber-900">
            {spareExtensions.length === 1
              ? 'An extension that rings for nothing'
              : `${spareExtensions.length} extensions that ring for nothing`}
          </h3>
          <ul className="mt-2 space-y-1.5">
            {spareExtensions.map((a) => (
              <li key={a.id} className="flex flex-wrap items-baseline gap-x-3 text-sm">
                <Link
                  to={`/extensions/${a.id}`}
                  state={backHere}
                  className="font-mono text-blue-700 hover:underline"
                >
                  {a.username}
                </Link>
                <span className="text-xs text-amber-900">
                  {isDuplicate(a)
                    ? <>a second copy of an extension that already rings. Remove this one — attaching it would ring the same phone twice.</>
                    : <>no number rings it. Attach it on a line&rsquo;s Routing tab, or remove it.</>}
                </span>
              </li>
            ))}
          </ul>
          {/* Only when attaching is actually the advice. A list of duplicates told to attach
              them is how one bad row becomes two. */}
          {subscriberId && spareExtensions.some((a) => !isDuplicate(a)) && (
            <p className="mt-2 text-xs text-amber-900">
              Attach one by opening a line above and using{' '}
              <span className="font-medium">Add an extension</span> on its Routing tab.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
